/**
 * @fileoverview Health Scenario Card
 * @description Renders controls and endpoint results for one Azure Function scenario.
 */

import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Play, RefreshCw, Star } from 'lucide-react';
import { EndpointStatusPanel } from './EndpointStatusPanel';
import { HEALTH_ENVIRONMENTS, PROBE_KINDS } from '../constants';
import { endpointExists, isEndpointCompliant } from '../hooks/useAzureFunctionHealth';

function getAzureFunctionUrl(widget, probeKind) {
  // Catalog availability takes precedence over generated URLs (Created or modified by CoPilot AI Agent)
  if (!endpointExists(widget, probeKind)) return 'Unavailable';
  return widget.endpointUrls?.[widget.selectedEnvironment]?.[probeKind] || 'Unavailable';
}

/**
 * Render a complete Azure Function health scenario widget.
 * @param {Object} props - Component properties and event handlers.
 * @returns {JSX.Element} Scenario card.
 */
export function HealthScenarioCard({
  widget,
  onEnvironmentChange,
  onRunEndpoint,
  onRunAll,
  onClearFailure,
  onToggleFavorite,
  onNotifyChange,
}) {
  const isAvailable = widget.probeEnabled
    && !widget.probeDisabledEnvironments.includes(widget.selectedEnvironment);
  // Display the distinct upstream paths for each probe kind (Created or modified by CoPilot AI Agent)
  const headFunctionUrl = getAzureFunctionUrl(widget, PROBE_KINDS.HEAD);
  const healthFunctionUrl = getAzureFunctionUrl(widget, PROBE_KINDS.HEALTH);

  return (
    <Paper className={`afh-scenario ${isAvailable ? '' : 'afh-scenario--muted'}`} elevation={0}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        {/* Created or modified by CoPilot AI Agent */}
        <Stack alignItems="flex-start" spacing={1}>
          <Box className="afh-scenario-title">
            <Typography variant="h6" component="h2">
              {widget.cardDisplayName || widget.displayName}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              aria-label={widget.azureFunctionId
                ? widget.isFavorite ? 'Remove from favorites' : 'Add to favorites'
                : 'Favorites unavailable for this endpoint'}
              title={widget.azureFunctionId
                ? widget.isFavorite ? 'Remove from favorites' : 'Add to favorites'
                : 'Favorites unavailable for this endpoint'}
              disabled={!widget.azureFunctionId || widget.isNotifySaving || widget.isFavoriteSaving}
              onClick={() => onToggleFavorite(widget.id)}
              size="small"
            >
              <Star
                size={22}
                fill={widget.isFavorite ? '#F5C542' : '#FFFFFF'}
                color="#111111"
                strokeWidth={2}
              />
            </IconButton>
            {/* Created or modified by CoPilot AI Agent */}
            {widget.isFavorite && (
              <Tooltip title="By enabling notifications, you will receive an email any time this function fails in the TEST environment only, QA and PROD support will be enabled in the future." arrow>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={Boolean(widget.notify)} disabled={widget.isNotifySaving || widget.isFavoriteSaving} onChange={(event) => onNotifyChange(widget.id, event.target.checked)} />}
                    label="Notify"
                  />
                </Box>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        <Chip
          size="small"
          color={isEndpointCompliant(widget) ? 'success' : 'warning'}
          label={isEndpointCompliant(widget) ? 'Endpoint compliant' : 'Endpoint gap'}
        />
      </Stack>

      <Stack className="afh-toolbar" direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <FormControl size="small" className="afh-environment-control">
          <InputLabel id={`environment-${widget.id}`}>Environment</InputLabel>
          <Select
            labelId={`environment-${widget.id}`}
            value={widget.selectedEnvironment}
            label="Environment"
            onChange={(event) => onEnvironmentChange(widget.id, event.target.value)}
          >
            {HEALTH_ENVIRONMENTS.map((environment) => (
              <MenuItem key={environment} value={environment}>{environment}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<Play size={16} />}
          disabled={!isAvailable}
          onClick={() => onRunAll(widget.id)}
        >
          Run available
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshCw size={16} />}
          disabled={!endpointExists(widget, PROBE_KINDS.HEAD) || widget.head.isLoading}
          onClick={() => onRunEndpoint(widget.id, PROBE_KINDS.HEAD)}
        >
          {widget.head.isLoading ? 'Running Head' : 'Run Head'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshCw size={16} />}
          disabled={!endpointExists(widget, PROBE_KINDS.HEALTH) || widget.health.isLoading}
          onClick={() => onRunEndpoint(widget.id, PROBE_KINDS.HEALTH)}
        >
          {widget.health.isLoading ? 'Running Health' : 'Run Health'}
        </Button>
      </Stack>

      <Box className="afh-route-meta">
        <Typography><strong>Head path:</strong> {headFunctionUrl}</Typography>
        <Typography><strong>Health path:</strong> {healthFunctionUrl}</Typography>
      </Box>

      <Box className="afh-endpoint-grid">
        <EndpointStatusPanel widget={widget} probeKind={PROBE_KINDS.HEAD} onClearFailure={onClearFailure} />
        <EndpointStatusPanel widget={widget} probeKind={PROBE_KINDS.HEALTH} onClearFailure={onClearFailure} />
      </Box>
    </Paper>
  );
}